import {
    DeliveryDateGroup,
    DeliveryListItem,
    DeliveryListTableItem,
    MontageListItem,
    WeekDay,
} from '@/modules/Logistics/interfaces/LogisticsInterfaces';
import moment from 'moment';
import i18n from '@/i18n';
import { formatDateToDateString } from '@/helpers/LocaleHelper';
import { ordersColumnData } from '@/helpers/TableHelper';
import {
    getInternalOrderDates,
    getInternalOrderDeliveryDate,
    getInternalOrderDeliveryDateLabel,
    sortDeliveryOrdersByStatus,
} from '@/modules/Logistics/helpers/DeliveryOrderHelper';
import type {
    DeliveryInternalOrder,
    DeliveryOrder,
    DeliveryOrderTableRow,
} from '@/modules/Logistics/interfaces/DeliveryOrderInterfaces';

// Re-export for backwards compatibility
export { DeliveryDateGroup, DeliveryListItem, DeliveryListTableItem, MontageListItem };

/**
 * Determine the aggregate printed status for a date group.
 * A date is considered "printed"/completed only when ALL of its delivery lists are printed.
 */
export function computeDatePrintedStatus(lists: Array<{ printed?: string }>): string {
    if (!lists.length) {
        return '❌';
    }
    return lists.every((item) => item.printed === '✅') ? '✅' : '❌';
}

/**
 * Filter delivery date groups by search query.
 * Matches on the date group itself, or on any delivery list within the date.
 */
export function filterDeliveryDateGroups(groups: DeliveryDateGroup[], query: string): DeliveryDateGroup[] {
    if (!query) {
        return groups;
    }
    const lowerQuery = query.toLowerCase();
    return groups.reduce<DeliveryDateGroup[]>((acc, group) => {
        const groupMatches =
            group?.date?.toLowerCase().includes(lowerQuery) ||
            group?.weekday?.toLowerCase().includes(lowerQuery) ||
            group?.number?.toLowerCase().includes(lowerQuery);

        if (groupMatches) {
            acc.push(group);
            return acc;
        }

        const matchingChildren = group.children.filter(
            (item) =>
                item?.date?.toLowerCase().includes(lowerQuery) ||
                item?.id?.toString().includes(lowerQuery) ||
                item?.printed?.toLowerCase().includes(lowerQuery),
        );

        if (matchingChildren.length) {
            acc.push({ ...group, children: matchingChildren });
        }

        return acc;
    }, []);
}

/**
 * Table columns for delivery lists - Date, Weekday, Number, Driver, Vehicle, Printed.
 * Rows are grouped by date; the Number column shows how many delivery lists
 * belong to the date (only for the date row), and Printed reflects the aggregate status.
 */
export const DELIVERY_LIST_TABLE_COLUMNS = () => {
    return [
        {
            title: i18n.t('Delivery date'),
            dataIndex: 'date',
            key: 'date',
            sorter: (a: DeliveryDateGroup, b: DeliveryDateGroup) => {
                return moment(a.date, 'DD.MM.YYYY').valueOf() - moment(b.date, 'DD.MM.YYYY').valueOf();
            },
            defaultSortOrder: 'descend' as const,
            width: 180,
        },
        {
            title: i18n.t('Weekday'),
            dataIndex: 'weekday',
            key: 'weekday',
            width: 180,
        },
        {
            title: i18n.t('Driver'),
            dataIndex: 'driver',
            key: 'driver',
            width: 180,
        },
        {
            title: i18n.t('Vehicle'),
            dataIndex: 'vehicle',
            key: 'vehicle',
            width: 180,
        },

        {
            title: i18n.t('No. of delivery runs'),
            dataIndex: 'number',
            key: 'number',
            width: 48,
        },
        {
            title: i18n.t('No. of addresses'),
            dataIndex: 'deliveriesCount',
            key: 'deliveriesCount',
            width: 48,
        },
        {
            title: i18n.t('Printed'),
            dataIndex: 'printed',
            key: 'printed',
            width: 48,
        },
        {
            title: '',
            key: 'downloadPDF',
            width: 48,
            scopedSlots: { customRender: 'downloadPDF' },
            dataIndex: 'downloadPDF',
        },
    ];
};

type DeliveryOrderSorter = (
    firstOrder: DeliveryOrderTableRow,
    secondOrder: DeliveryOrderTableRow,
    sortOrder?: 'ascend' | 'descend',
) => number;

export interface DeliveryOrderSort {
    columnKey?: string;
    order?: 'ascend' | 'descend';
}

function getDeliveryOrderColumns(activeSort?: DeliveryOrderSort) {
    const columns = ordersColumnData
        .filter(({ key }) => key !== 'price')
        .map(({ key, name }) => {
            if (key === 'orderNumber') {
                return {
                    title: i18n.t('Order number'),
                    dataIndex: key,
                    key,
                    sorter: (firstOrder: DeliveryOrderTableRow, secondOrder: DeliveryOrderTableRow) =>
                        compareText(firstOrder.orderNumber, secondOrder.orderNumber),
                };
            }

            if (key === 'orderStatusId') {
                return {
                    title: i18n.t(name),
                    dataIndex: 'orderStatusName',
                    key: 'orderStatusName',
                    sorter: sortDeliveryOrdersByStatus,
                    defaultSortOrder: 'ascend' as const,
                };
            }

            if (key === 'productsInfo') {
                return {
                    title: i18n.t(name),
                    key: 'internalOrdersCount',
                    customRender: (_: unknown, order: DeliveryOrder) => order.projectInternalOrders.length,
                    sorter: (firstOrder: DeliveryOrderTableRow, secondOrder: DeliveryOrderTableRow) =>
                        firstOrder.projectInternalOrders.length - secondOrder.projectInternalOrders.length,
                };
            }

            return {
                title: i18n.t(name),
                dataIndex: key,
                key,
                sorter: (firstOrder: DeliveryOrderTableRow, secondOrder: DeliveryOrderTableRow) => {
                    if (key === 'orderCreatedAt') {
                        return compareDate(firstOrder.orderCreatedAt, secondOrder.orderCreatedAt);
                    }

                    return compareText(
                        String(firstOrder[key as keyof DeliveryOrderTableRow] || ''),
                        String(secondOrder[key as keyof DeliveryOrderTableRow] || ''),
                    );
                },
                ...(key === 'orderCreatedAt'
                    ? {
                          customRender: (date: string) => formatDateToDateString(date) || '-',
                      }
                    : {}),
            };
        });

    const clientColumnIndex = columns.findIndex(({ key }) => key === 'clientName');
    columns.splice(clientColumnIndex + 1, 0, {
        title: i18n.t('Address'),
        dataIndex: 'address',
        key: 'address',
        sorter: (firstOrder: DeliveryOrderTableRow, secondOrder: DeliveryOrderTableRow) =>
            compareText(firstOrder.address, secondOrder.address),
    });

    const statusColumnIndex = columns.findIndex(({ key }) => key === 'orderStatusName');
    columns.splice(statusColumnIndex + 1, 0, {
        title: i18n.t('Delivery date'),
        dataIndex: 'deliveryDate',
        key: 'deliveryDate',
        customRender: (date: string) => formatDateToDateString(date) || '-',
        sorter: (firstOrder: DeliveryOrderTableRow, secondOrder: DeliveryOrderTableRow) =>
            compareDate(firstOrder.deliveryDate, secondOrder.deliveryDate),
    });

    return columns.map((column) => ({
        ...column,
        sorter: keepSelectedOrdersFirst(column.sorter),
        ...(activeSort
            ? {
                  sortOrder: activeSort.columnKey === column.key ? activeSort.order || null : null,
              }
            : {}),
    }));
}

function keepSelectedOrdersFirst(sorter: DeliveryOrderSorter): DeliveryOrderSorter {
    return (firstOrder, secondOrder, sortOrder) => {
        const selectionOrder = Number(secondOrder.isSelected) - Number(firstOrder.isSelected);

        if (selectionOrder !== 0) {
            return sortOrder === 'descend' ? -selectionOrder : selectionOrder;
        }

        return sorter(firstOrder, secondOrder);
    };
}

function compareText(firstValue: string, secondValue: string): number {
    return firstValue.localeCompare(secondValue, undefined, {
        numeric: true,
        sensitivity: 'base',
    });
}

function compareDate(firstDate: string, secondDate: string): number {
    const firstMoment = moment(firstDate);
    const secondMoment = moment(secondDate);
    const firstValue = firstMoment.isValid() ? firstMoment.valueOf() : 0;
    const secondValue = secondMoment.isValid() ? secondMoment.valueOf() : 0;

    return firstValue - secondValue;
}

export function getDeliveryOrderSelectorColumns(activeSort?: DeliveryOrderSort) {
    return [
        {
            title: '',
            key: 'expand',
            width: 48,
            scopedSlots: { customRender: 'expand' },
        },
        ...getDeliveryOrderColumns(activeSort),
    ];
}

export function getSelectedOrderColumns() {
    const columns = getDeliveryOrderSelectorColumns();
    const addressColumnIndex = columns.findIndex(({ key }) => key === 'address');

    return [
        ...columns.slice(0, addressColumnIndex + 1),
        {
            title: i18n.t('Delivery region'),
            dataIndex: 'deliveryRegion',
            key: 'deliveryRegion',
            sorter: (firstOrder: DeliveryOrderTableRow, secondOrder: DeliveryOrderTableRow) =>
                compareText(firstOrder.deliveryRegion, secondOrder.deliveryRegion),
        },
        ...columns.slice(addressColumnIndex + 1),
    ];
}

export function getDeliveryInternalOrderColumns(order: DeliveryOrder) {
    return [
        {
            title: i18n.t('Order number'),
            dataIndex: 'orderNumber',
            key: 'orderNumber',
        },
        {
            title: i18n.t('Status'),
            key: 'orderStatus',
            customRender: (_: unknown, internalOrder: DeliveryInternalOrder) => {
                if (typeof internalOrder.orderStatus === 'string') {
                    return internalOrder.orderStatus;
                }

                return internalOrder.orderStatus?.name || internalOrder.orderStatus?.code || '-';
            },
        },
        {
            title: i18n.t('Technologist date'),
            key: 'technologistDate',
            customRender: (_: unknown, internalOrder: DeliveryInternalOrder) =>
                formatDateToDateString(getInternalOrderDates(internalOrder)?.technologistDate || '') || '-',
        },
        {
            title: i18n.t('Planned production start'),
            key: 'inProduction',
            customRender: (_: unknown, internalOrder: DeliveryInternalOrder) =>
                formatDateToDateString(getInternalOrderDates(internalOrder)?.inProduction || '') || '-',
        },
        {
            title: i18n.t(getInternalOrderDeliveryDateLabel(order.projectInternalOrders)),
            key: 'deliveryDate',
            customRender: (_: unknown, internalOrder: DeliveryInternalOrder) =>
                formatDateToDateString(getInternalOrderDeliveryDate(internalOrder)) || '-',
        },
        {
            title: i18n.t('Production date'),
            key: 'manufacturedDate',
            customRender: (_: unknown, internalOrder: DeliveryInternalOrder) =>
                formatDateToDateString(getInternalOrderDates(internalOrder)?.manufacturedDate || '') || '-',
        },
    ];
}

export const DELIVERY_ADDRESS_WEEK_DAYS: WeekDay[] = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
];

export const MONTAGE_TEAMS_TABLE_COLUMNS = [
    { title: i18n.t('Name'), dataIndex: 'name', key: 'name', type: 'string' as const },
    { title: i18n.t('Last Name'), dataIndex: 'lastName', key: 'lastName', type: 'string' as const },
    { title: i18n.t('Driver'), dataIndex: 'driver', key: 'driver', type: 'boolean' as const },
    { title: i18n.t('Team Leader'), dataIndex: 'teamLeader', key: 'teamLeader', type: 'boolean' as const },
    { title: i18n.t('Installer'), dataIndex: 'installer', key: 'installer', type: 'boolean' as const },
    { title: i18n.t('Vehicle'), dataIndex: 'vehicleRegistration', key: 'vehicleRegistration' },
];

export const MONTAGE_TEAMS_FORM_COLUMNS = [
    { title: i18n.t('Name'), dataIndex: 'name', key: 'name', type: 'string' as const },
    { title: i18n.t('Last Name'), dataIndex: 'lastName', key: 'lastName', type: 'string' as const },
    { title: i18n.t('Driver'), dataIndex: 'driver', key: 'driver', type: 'boolean' as const },
    { title: i18n.t('Team Leader'), dataIndex: 'teamLeader', key: 'teamLeader', type: 'boolean' as const },
    { title: i18n.t('Installer'), dataIndex: 'installer', key: 'installer', type: 'boolean' as const },
    { title: i18n.t('Vehicle'), dataIndex: 'vehicleId', key: 'vehicleId', type: 'dropdown' as const },
];

/**
 * Montage Teams table columns - Registracija (Registration), Tip (Type), Funkcija (Function), Voznik (Driver)
 */
export const VEHICLES_LIST_TABLE_COLUMNS = [
    { title: i18n.t('Registration'), dataIndex: 'registration', key: 'registration', type: 'string' as const },
    { title: i18n.t('Type'), dataIndex: 'type', key: 'type', type: 'dropdown' as const },
    { title: i18n.t('Purpose'), dataIndex: 'purpose', key: 'purpose', type: 'dropdown' as const },
    { title: i18n.t('Driver'), dataIndex: 'driver', key: 'driver', type: 'readonly' as const },
];

export const VEHICLE_TYPE_OPTIONS = [
    { value: 'Kombi', label: i18n.t('Van') },
    { value: 'Kamion', label: i18n.t('Truck') },
];

export const VEHICLE_PURPOSE_OPTIONS = [
    { value: 'Montaža', label: i18n.t('Installation') },
    { value: 'Razvoz', label: i18n.t('Montage Delivery') },
];

/**
 * Filter montage lists by search query
 */
export function filterMontageLists(lists: MontageListItem[], query: string): MontageListItem[] {
    if (!query) {
        return lists;
    }
    const lowerQuery = query.toLowerCase();
    return lists.filter(
        (item) =>
            item?.number?.toLowerCase().includes(lowerQuery) ||
            item?.installer?.toLowerCase().includes(lowerQuery) ||
            item?.otherInstallers?.toLowerCase().includes(lowerQuery) ||
            item?.preparedBy?.toLowerCase().includes(lowerQuery) ||
            item?.vehicle?.toLowerCase().includes(lowerQuery) ||
            item?.date?.toLowerCase().includes(lowerQuery) ||
            item?.weekday?.toLowerCase().includes(lowerQuery) ||
            item?.id?.toString().includes(lowerQuery) ||
            item?.printed?.toLowerCase().includes(lowerQuery),
    );
}

/**
 * Table columns for montage lists
 */
export const MONTAGE_LIST_TABLE_COLUMNS = [
    {
        title: i18n.t('Montage date'),
        dataIndex: 'date',
        key: 'date',
        sorter: (a: MontageListItem, b: MontageListItem) =>
            moment(a.date, 'DD.MM.YYYY').valueOf() - moment(b.date, 'DD.MM.YYYY').valueOf(),
        defaultSortOrder: 'descend' as const,
    },
    {
        title: i18n.t('Weekday'),
        dataIndex: 'weekday',
        key: 'weekday',
    },
    {
        title: i18n.t('Number'),
        dataIndex: 'number',
        key: 'number',
    },
    {
        title: i18n.t('Installer'),
        dataIndex: 'installer',
        key: 'installer',
    },
    {
        title: i18n.t('Other Installers'),
        dataIndex: 'otherInstallers',
        key: 'otherInstallers',
    },
    {
        title: i18n.t('Prepared By'),
        dataIndex: 'preparedBy',
        key: 'preparedBy',
    },
    {
        title: i18n.t('Vehicle'),
        dataIndex: 'vehicle',
        key: 'vehicle',
    },
    {
        title: i18n.t('Printed'),
        dataIndex: 'printed',
        key: 'printed',
    },
];
